<template>
  <VueFinalModal
    v-model="isOpen"
    content-transition="vfm-fade"
    :click-to-close="true"
    class="flex justify-center items-center p-6"
    content-class="relative w-full max-w-6xl h-[85vh] bg-[#fafbfc] rounded-[40px] shadow-2xl flex overflow-hidden z-50 ring-1 ring-black/5"
    @close="close"
  >
    <div class="flex w-full h-full min-h-0">
      <div class="w-full lg:w-[32%] bg-[#1c4848] relative flex flex-col p-8 text-white shrink-0 overflow-hidden">
        <div class="relative z-10 flex items-center gap-3 mb-6 opacity-80">
          <div class="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-[#ecd79f]">
            <IconCatchStar class="w-[18px] h-[18px]" />
          </div>
          <span class="text-xs font-bold tracking-[0.2em] uppercase">Preview</span>
        </div>
        <div class="relative z-10 flex-1 flex items-center justify-center">
          <div class="text-center text-white/40 text-sm">Catch the Star</div>
        </div>
      </div>
      <div class="flex-1 flex flex-col bg-[#fafbfc] relative overflow-hidden min-w-0">
        <header class="px-8 py-6 border-b border-[#1c4848]/5 flex items-center justify-between bg-white/50 backdrop-blur-sm sticky top-0 z-30">
          <h2 class="text-xl font-bold text-[#1c4848] tracking-tight">Create Catch the Star</h2>
          <div class="flex gap-3">
            <button type="button" class="px-4 py-2 rounded-xl text-xs font-bold text-[#1c4848]/60 hover:bg-[#1c4848]/5" @click="close">Cancel</button>
            <button type="button" :disabled="!canSave" class="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#1c4848] disabled:opacity-50 flex items-center gap-2" @click="save">Save Game</button>
          </div>
        </header>
        <div class="flex-1 overflow-y-auto p-8">
          <form class="space-y-8 max-w-3xl mx-auto" @submit.prevent="save">
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">General</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div class="md:col-span-2">
                  <TextInput v-model="form.name" label="Game Title" placeholder="Catch the Star" input-id="catch-title" />
                </div>
                <div class="md:col-span-2">
                  <TextareaInput v-model="form.description" label="Description" :rows="3" textarea-id="catch-desc" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Difficulty</label>
                  <DifficultyBadge :difficulty="form.difficulty" editable @update:difficulty="form.difficulty = $event" />
                </div>
                <div>
                  <SelectInput v-model="form.languageId" label="Language" select-id="catch-language" :options="languageOptions" />
                </div>
              </div>
            </section>
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Config</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Lives</label>
                  <input v-model.number="form.lives" type="number" min="1" max="10" class="w-full bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm">
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Time limit (s)</label>
                  <input v-model.number="form.time_limit" type="number" min="10" max="300" class="w-full bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm">
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Spawn interval (s)</label>
                  <input v-model="form.spawn_interval" type="number" step="0.1" min="0.2" class="w-full bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm">
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-[#1c4848]/60 uppercase tracking-wider mb-1.5 ml-1">Spawn interval range</label>
                  <input v-model="form.spawn_interval_range" type="number" step="0.1" min="0" class="w-full bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 text-sm">
                </div>
              </div>
            </section>
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Image upload</h4>
              <UploadImageSizeSelect v-model="form.uploadSize" select-id="create-catch-upload-size" />
            </section>
            <section>
              <h4 class="text-xs font-bold text-[#1c4848] uppercase tracking-widest mb-4">Cover</h4>
              <div class="w-full h-40">
                <ImageDropzone v-model="form.coverFile" input-id="catch-cover" size="small" class="h-full w-full" />
              </div>
            </section>
          </form>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { computed, reactive, watch } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import TextInput from '@/components/forms/text/TextInput.vue';
import TextareaInput from '@/components/forms/textarea/TextareaInput.vue';
import ImageDropzone from '@/components/forms/dropzone/ImageDropzone.vue';
import SelectInput from '@/components/forms/select/SelectInput.vue';
import UploadImageSizeSelect from '@/components/forms/select/UploadImageSizeSelect.vue';
import DifficultyBadge from '@/components/app/dashboard/DifficultyBadge.vue';
import IconCatchStar from '@/components/app/dashboard/games/icons/IconCatchStar.vue';
import { useGamesStore } from '@/store/games/index.js';
import { useLanguagesStore } from '@/store/languages/index.js';
import { getLanguageLabel } from '@/utils/languageLabel.js';

const props = defineProps({ modelValue: { type: Boolean, default: false } });
const emit = defineEmits(['update:modelValue', 'close', 'saved']);

const isOpen = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) });
const gamesStore = useGamesStore();
const languagesStore = useLanguagesStore();

const form = reactive({
  name: '',
  description: '',
  difficulty: 1,
  languageId: '',
  lives: 3,
  time_limit: 60,
  spawn_interval: 0.75,
  spawn_interval_range: 0.4,
  uploadSize: '',
  coverFile: null,
});

const languageOptions = computed(() => {
  const list = languagesStore.languages ?? [];
  return [{ value: '', label: 'Select language' }, ...list.map((l) => ({ value: String(l.id), label: getLanguageLabel(l) }))];
});

watch(() => props.modelValue, (v) => { if (v) languagesStore.fetchLanguages(); });

const canSave = computed(() => Boolean(form.name?.trim() && form.languageId && form.coverFile instanceof File));

function close() {
  emit('update:modelValue', false);
  emit('close');
}

async function save() {
  if (!canSave.value) return;
  try {
    const payload = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      game_type: 'catch_star',
      difficulty: form.difficulty,
      language_id: form.languageId ? Number(form.languageId) : null,
      config: {
        lives: String(form.lives),
        time_limit: String(form.time_limit),
        spawn_interval: String(form.spawn_interval),
        spawn_interval_range: String(form.spawn_interval_range),
      },
      rating: null,
    };
    const created = await gamesStore.create(payload);
    await gamesStore.uploadCover(created.id, form.coverFile, form.uploadSize);
    emit('saved');
    close();
  } catch (err) {
    console.error(err);
    alert('Failed to save.');
  }
}
</script>
